import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { usePack } from '../contexts/PackContext';
import Button from './ui/Button';
import Icon from './AppIcon';

const PackPill = () => {
  const { activePack, setActivePack } = usePack();
  const navigate = (path) => {
    window.location.href = path;
  };
  const [showPackSelector, setShowPackSelector] = useState(false);

  // Pack options configuration with enhanced visuals
  const packOptions = [
    { 
      id: 'capital', 
      label: 'Capital', 
      description: 'Portfolio Intelligence Platform',
      color: 'indigo',
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-800',
      hoverColor: 'hover:bg-indigo-200',
      icon: 'TrendingUp',
      dashboardRoute: '/capital-portfolio-dashboard'
    },
    { 
      id: 'msp', 
      label: 'MSP', 
      description: 'Managed Service Provider',
      color: 'blue',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      hoverColor: 'hover:bg-blue-200',
      icon: 'Building2',
      dashboardRoute: '/msp-sector-performance'
    },
    { 
      id: 'dentist', 
      label: 'Dentist', 
      description: 'Dental Practice Management',
      color: 'green',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      hoverColor: 'hover:bg-green-200',
      icon: 'Stethoscope',
      dashboardRoute: '/home-dashboard'
    },
    { 
      id: 'creche', 
      label: 'Crèche', 
      description: 'Childcare Management',
      color: 'purple',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-800',
      hoverColor: 'hover:bg-purple-200',
      icon: 'Heart',
      dashboardRoute: '/creche/today'
    }
  ];

  // Get current pack configuration
  const currentPack = packOptions?.find(p => p?.id === activePack) || packOptions?.[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event?.target?.closest('.pack-selector-dropdown')) {
        setShowPackSelector(false);
      }
    };

    if (showPackSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPackSelector]);

  const handlePackChange = (packId) => {
    if (packId !== activePack) {
      setActivePack(packId);
      setShowPackSelector(false);
      
      // ROUTE TO APPROPRIATE DASHBOARD BASED ON PACK
      const selectedPack = packOptions?.find(p => p?.id === packId);
      if (selectedPack?.dashboardRoute) {
        navigate(selectedPack?.dashboardRoute);
      }
      
      console.log(`Pack switched to: ${packId} -> routed to: ${selectedPack?.dashboardRoute}`);
    }
  };

  return (
    <div className="relative pack-selector-dropdown">
      <Button
        variant="ghost"
        onClick={() => setShowPackSelector(!showPackSelector)}
        className={`inline-flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
          currentPack?.bgColor
        } ${currentPack?.textColor} ${currentPack?.hoverColor}`}
      >
        <Icon name={currentPack?.icon} size={16} />
        <span className="hidden sm:inline">Pack:</span>
        <span>{currentPack?.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
          showPackSelector ? 'rotate-180' : ''
        }`} />
      </Button>
      {showPackSelector && (
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-3">
            <div className="text-xs font-medium text-gray-500 mb-3 px-2">
              Choose Your Pack
            </div>
            {packOptions?.map((pack) => (
              <button
                key={pack?.id}
                onClick={() => handlePackChange(pack?.id)}
                className={`w-full flex items-center space-x-3 p-3 rounded-md text-left transition-all duration-200 hover:bg-gray-50 ${
                  pack?.id === activePack ? 'bg-gray-50 ring-2 ring-primary ring-opacity-20' : ''
                }`}
              >
                <div className={`w-10 h-10 ${pack?.bgColor} rounded-lg flex items-center justify-center`}>
                  <Icon name={pack?.icon} size={20} className={pack?.textColor} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{pack?.label}</div>
                  <div className="text-xs text-gray-500">{pack?.description}</div>
                </div>
                {pack?.id === activePack && (
                  <div className="w-3 h-3 bg-primary rounded-full flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-gray-100 px-3 py-2">
            <div className="text-xs text-gray-400 text-center">
              Switch between different business packs
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackPill;